import React, { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchUserTable } from "../../features/commonSlice/commonSlice";
import { resetPasswordByAdmin } from "../../features/userSlice/userSlice";

const ResetUserPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const { usersTableData = [], status: commonStatus } = useSelector(
    (state) => state.influencer,
  );

  const [errors, setErrors] = useState({});
  const [errorData, setErrorData] = useState([]);
  const [loginError, setLoginError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirm_password: "",
  });

  const selectedUser = useMemo(() => {
    if (location.state) {
      return location.state;
    }

    return (
      usersTableData.find((user) => String(user.id) === String(id)) || null
    );
  }, [id, location.state, usersTableData]);

  useEffect(() => {
    if (!location.state && !usersTableData.length) {
      dispatch(fetchUserTable());
    }
  }, [dispatch, location.state, usersTableData.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
    setLoginError("");

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReset = () => {
    setFormData({
      password: "",
      confirm_password: "",
    });
    setErrors({});
    setErrorData([]);
    setLoginError("");
  };

  const mapErrors = (errorList) => {
    const errorMap = {};

    (errorList || []).forEach((error) => {
      if (!errorMap[error.param]) {
        errorMap[error.param] = error.msg;
      }
    });

    return errorMap;
  };

  useEffect(() => {
    setErrors(mapErrors(errorData));
  }, [errorData]);

  const handleBack = () => {
    navigate("/registered-user-list");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedUser?.id) {
      toast.error("Selected user details were not found.");
      return;
    }

    if (formData.password !== formData.confirm_password) {
      setErrors({ confirm_password: "Passwords do not match" });
      return;
    }

    try {
      setIsLoading(true);

      await dispatch(
        resetPasswordByAdmin({
          user_id: String(selectedUser.id),
          password: formData.password,
        }),
      )
        .unwrap()
        .then((result) => {
          if (result.status) {
            toast.success(result.message, {
              onClose: () => {
                handleReset();
                navigate("/registered-user-list");
              },
            });
            return;
          }

          setLoginError(result.message || "Something went wrong");
          setErrorData(result.form_err || []);
        })
        .catch((err) => {
          setLoginError(err?.message || "Error occurred");
          setErrorData(err?.form_err || []);
          toast.error(err?.message || "Error occurred");
        });
    } catch (error) {
      console.error("An error occurred:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const isUserLoading = !selectedUser && commonStatus === "loading";

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Reset User Password</h4>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="card shadow-sm col-8">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Reset User Password</h5>

            <Button variant="outline-secondary" size="sm" onClick={handleBack}>
              ← Back
            </Button>
          </div>

          <div className="card-body">
            {isUserLoading ? (
              <div className="text-center py-4">Loading user details...</div>
            ) : !selectedUser ? (
              <div className="text-center py-4">
                <p className="mb-3">Selected user details are not available.</p>
                <Button variant="outline-secondary" onClick={handleBack}>
                  Back to User List
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label mb-2">Name</label>
                    <input
                      type="text"
                      className="form-control pe-5"
                      value={selectedUser.name || ""}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label mb-2">Email</label>
                    <input
                      type="email"
                      className="form-control pe-5"
                      value={selectedUser.email || ""}
                      readOnly
                    />
                    {errors.email && (
                      <div className="text-danger">{errors.email}</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label mb-2">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control pe-5"
                        name="password"
                        placeholder="Enter new password"
                        value={formData.password}
                        onChange={handleChange}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute end-0 top-0 text-muted"
                        onClick={() => setShowPassword((prev) => !prev)}
                      >
                        <i
                          className={`ri-${showPassword ? "eye-off-fill" : "eye-fill"}`}
                        ></i>
                      </button>
                    </div>
                    {errors.password && (
                      <div className="text-danger">{errors.password}</div>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label mb-2">
                      Confirm Password <span className="text-danger">*</span>
                    </label>
                    <div className="position-relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        className="form-control pe-5"
                        name="confirm_password"
                        placeholder="Enter confirm password"
                        value={formData.confirm_password}
                        onChange={handleChange}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="btn btn-link position-absolute end-0 top-0 text-muted"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                      >
                        <i
                          className={`ri-${showConfirmPassword ? "eye-off-fill" : "eye-fill"}`}
                        ></i>
                      </button>
                    </div>
                    {errors.confirm_password && (
                      <div className="text-danger">
                        {errors.confirm_password}
                      </div>
                    )}
                  </div>

                  <div className="col-12 mb-3">
                    <div className="alert alert-warning mb-0">
                      This will reset the selected user&apos;s password
                      immediately.
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end align-items-center gap-2">
                  <p className="text-danger mb-0 me-auto">
                    {!errorData.length && loginError}
                  </p>
                  <Button variant="danger" onClick={handleReset}>
                    Reset
                  </Button>
                  <Button
                    variant="success"
                    type="submit"
                    disabled={
                      isLoading ||
                      !formData.password ||
                      !formData.confirm_password
                    }
                  >
                    {isLoading ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      "Update Password"
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetUserPassword;
